<template>
  <div class="qty-component d-flex justify-content-around">
    <button class="rmv-qty-btn text-white" @click="removeFromCart">
      <font-awesome-icon icon="fa-solid fa-minus"/>
    </button>
    <input class="qty-input"
           v-model.number="saleLine.Qty"
           type="number"
           step="0.0001"
           @focus="captureQty"
           @input="onQtyChanged">
    <button class="add-qty-btn text-white" @click="addToCart">
      <font-awesome-icon icon="fa-solid fa-plus"/>
    </button>
  </div>
</template>

<script>
export default {
  name: "CurrentSaleQty",
  data() {
    return {
      previousQty: null,
    }
  },
  props: {
    saleLine: {
      type: Object,
      required: true
    }
  },
  methods: {
    captureQty() {
      this.previousQty = Number(this.saleLine.Qty);
    },
    onQtyChanged(event) {
      let rawQty = event && event.target ? event.target.value : this.saleLine.Qty;
      let newQty = Number(rawQty);
      if (rawQty === '' || !Number.isFinite(newQty) || newQty < 0) {
        return;
      }
      // Normalise the qty to 4 dp so what is stored in state, displayed
      // in the cart, and ultimately sent to the server in the invoice
      // payload is the same value. The user is allowed to type 5+ dp via
      // step="0.0001"; the model collapses to 4 dp on every keystroke.
      newQty = this.$utils.roundTo(newQty, 4);
      let oldQty = Number(this.previousQty);
      if (!Number.isFinite(oldQty)) {
        oldQty = newQty;
      }
      this.$store.commit('syncSaleLineInstructionsWithQty', {
        index: this.saleLine.index,
        oldQty: oldQty,
        newQty: newQty,
      });
      // Recalculate Line total preserving the existing Unit price
      // Do NOT call updateSaleLinePrice here as it re-fetches the stock price
      // which resets manually set prices back to the database value (e.g., K 0)
      this.saleLine.Qty = newQty;
      // Round the Line money value to 2 dp so what is stored in state,
      // shown in the cart, and sent to the server in the invoice payload
      // is the same clean value (avoids IEEE-754 noise like 0.48840500000000003).
      let lineTotal = this.$utils.roundTo(Number(this.saleLine.Unit) * newQty, 2)
                    - Number(this.saleLine.Discount || 0);
      this.saleLine.Line = this.$utils.roundTo(lineTotal, 2);
      this.previousQty = newQty;
    },
    addToCart() {
      return this.$store.dispatch('getStockSetting', this.saleLine.Plu)
          .then(async (set) => {
            if (!set) {
              set = { cookingLink: false, askForPrice: false, askForDescription: false, askForBoth: false };
            }
            // Preserve the existing sale-line unit price precision
            // (e.g. 0.234). The previous toFixed(2) was truncating the
            // price when adding another unit of the same item.
            let price = Number(this.saleLine.Unit);
            let description = this.saleLine.Description;

            if (set.askForPrice){
              price = (await this.askForPrice(this.saleLine));
            }
            if (set.cookingLink) {
               return this.$router.push({name: 'instruction', params: {plu: this.saleLine.Plu}})
            }

            let obj = {
              Plu: this.saleLine.Plu,
              Description: description,
              Qty: 1,
              Unit: price,
              // Line is a money total — keep at 2 dp.
              Line: Number((price * 1).toFixed(2)),
              Discount: 0,
              vat: 0,
              instructions: []
            }
            return this.$store.commit('addToCurrentSaleLines', obj)
          })
    },

    removeFromCart() {
      this.$store.commit("voidItem", this.saleLine)
    },

    askForPrice(item) {
      return Swal.fire({
        allowOutsideClick: false,
        showCancelButton: true,
        showLoaderOnConfirm: true,
        title: `Set Selling Price`,
        html: `<h6 >Description : ${item.Description}</h6>
               <h6 >Current Price : ${item.Unit}</h6>
               <input type="number" step="0.001" id="selling-prc" class="swal2-input" placeholder="Selling Price" style="max-width: 70%">`,
        confirmButtonText: 'Set Price',
        focusConfirm: false,
        preConfirm: async () => {
          let value = Swal.getPopup().querySelector('#selling-prc').value
          if (!value) {
            Swal.showValidationMessage(`Please enter a valid Selling Price`)
          }
          return parseFloat(value);
        }
      }).then(result=>{
        if (result.isConfirmed){
          return result.value;
        }
        // Preserve the existing sale-line unit price precision instead
        // of truncating to 2 dp.
        return Number(item.Unit);
      })
    },
  }
}
</script>

<style scoped>
.qty-component {
  gap: 5px;
}

.qty-input {
  width: 75px;
  border: 1px solid #C9CACF;
  border-radius: 10px;
  text-align: center;
}

.add-qty-btn, .rmv-qty-btn {
  border-radius: 10px;
  width: 30px;
  height: 30px;
}

button {
  background: #9ccc65;
}
</style>
